import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Facebook, Instagram, Youtube, User, LogOut, ChevronDown, Moon, Sun } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useCustomerAuth } from '../../context/CustomerAuthContext';
import { Button } from '../ui/button';
import { CartButton } from '../ShoppingCart';
import { SeasonalLogoAccessory } from '../../seasonal/SeasonalOverlay';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ShrimpLogo = () => (
  <img loading="lazy" 
    src={`${API}/logo/no_text`} 
    alt="Just Keep Shrimping Logo" 
    className="w-12 h-12 object-contain"
    width="48"
    height="48"
  />
);

// Dropdown component for desktop
const DropdownMenu = ({ label, items, isActive }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`nav-link font-body text-sm font-medium transition-colors flex items-center gap-1 ${
          isActive ? 'text-shrimp-green' : 'text-shrimp-text hover:text-shrimp-dark'
        }`}
      >
        {label}
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-48 bg-surface rounded-lg shadow-lg border border-shrimp-border/50 py-2 z-50">
          {items.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setIsOpen(false)}
              className="block px-4 py-2 text-sm text-shrimp-text hover:bg-shrimp-beige hover:text-shrimp-dark transition-colors"
            >
              {item.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

// Mobile dropdown component
const MobileDropdown = ({ label, items, isActive, onItemClick }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full px-4 py-3 rounded-lg font-body font-medium transition-colors flex items-center justify-between ${
          isActive ? 'bg-shrimp-beige text-shrimp-dark' : 'text-shrimp-text hover:bg-shrimp-beige/50'
        }`}
      >
        {label}
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div className="ml-4 mt-1 space-y-1">
          {items.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => {
                setIsOpen(false);
                onItemClick();
              }}
              className="block px-4 py-2 rounded-lg text-sm text-shrimp-text hover:bg-shrimp-beige/50 transition-colors"
            >
              {item.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [settings, setSettings] = useState({});
  const [isDark, setIsDark] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('theme') === 'dark';
    }
    return false;
  });
  const location = useLocation();
  const { user } = useAuth();
  const { customer, isAuthenticated, logout: customerLogout } = useCustomerAuth();

  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('theme', isDark ? 'dark' : 'light');
  }, [isDark]);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings`);
        setSettings(response.data);
      } catch (error) {
        console.error('Failed to fetch settings');
      }
    };
    fetchSettings();
  }, []);

  // Navigation structure with dropdowns
  const shrimpRoomItems = [
    { path: '/shrimp-room', label: 'Shop' },
    { path: '/track', label: 'Track Your Order' },
    { path: '/gallery', label: 'Gallery' },
  ];

  const shrimpNeedsItems = [
    { path: '/recommendations', label: 'Our Recommendations' },
    { path: '/equipment', label: 'Equipment Reviews' },
  ];

  const aboutUsItems = [
    { path: '/about', label: 'Who We Are' },
    { path: '/blog', label: 'Science & Blog' },
    { path: '/updates', label: 'Updates' },
    { path: '/terms', label: 'Terms' },
  ];

  const isAboutActive = location.pathname === '/about' || location.pathname === '/terms' || 
                        location.pathname === '/blog' || location.pathname.startsWith('/blog/') ||
                        location.pathname === '/updates';
  const isShrimpRoomActive = location.pathname === '/shrimp-room' || location.pathname === '/gallery' || 
                             location.pathname.startsWith('/track') || location.pathname.startsWith('/shrimp/');
  const isShrimpNeedsActive = location.pathname === '/recommendations' || location.pathname === '/equipment';

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <header className="sticky top-0 z-50 glass border-b border-shrimp-border/50" data-testid="header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 logo-shrimp" data-testid="logo-link">
            <span className="relative inline-flex">
              <ShrimpLogo />
              <SeasonalLogoAccessory size={48} />
            </span>
            <div className="hidden sm:block">
              <span className="font-display text-xl font-semibold text-shrimp-dark block">
                Just Keep Shrimping
              </span>
              <span className="text-xs text-shrimp-text">
                Owned and Operated by: Sanjin Blazinic
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
            <Link
              to="/"
              className={`nav-link font-body text-sm font-medium transition-colors ${
                isActive('/') && location.pathname === '/'
                  ? 'text-shrimp-green' 
                  : 'text-shrimp-text hover:text-shrimp-dark'
              }`}
              data-testid="nav-home"
            >
              Home
            </Link>
            
            <DropdownMenu 
              label="Shrimp Room" 
              items={shrimpRoomItems} 
              isActive={isShrimpRoomActive}
            />

            <DropdownMenu 
              label="Shrimp Needs" 
              items={shrimpNeedsItems} 
              isActive={isShrimpNeedsActive}
            />
            
            <DropdownMenu 
              label="About Us" 
              items={aboutUsItems} 
              isActive={isAboutActive}
            />
            
            <Link
              to="/contact"
              className={`nav-link font-body text-sm font-medium transition-colors ${
                isActive('/contact') 
                  ? 'text-shrimp-green' 
                  : 'text-shrimp-text hover:text-shrimp-dark'
              }`}
              data-testid="nav-contact"
            >
              Contact
            </Link>
          </nav>

          {/* Social Links, Account & Cart */}
          <div className="hidden md:flex items-center gap-4">
            {settings.facebook_url && (
              <a 
                href={settings.facebook_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-shrimp-text hover:text-shrimp-green transition-colors"
                data-testid="social-facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
            )}
            {settings.instagram_url && (
              <a 
                href={settings.instagram_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-shrimp-text hover:text-shrimp-green transition-colors"
                data-testid="social-instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
            )}
            {settings.youtube_url && (
              <a 
                href={settings.youtube_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-shrimp-text hover:text-red-500 transition-colors"
                data-testid="social-youtube"
              >
                <Youtube className="w-5 h-5" />
              </a>
            )}
            <div className="w-px h-5 bg-shrimp-border mx-1" />

            {/* Dark mode toggle */}
            <button
              onClick={() => setIsDark(!isDark)}
              className="p-2 rounded-lg text-shrimp-text hover:text-shrimp-dark hover:bg-shrimp-beige transition-colors"
              data-testid="dark-mode-toggle"
              aria-label="Toggle dark mode"
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            
            {/* Customer Account */}
            {isAuthenticated ? (
              <Link 
                to="/my-account" 
                className="flex items-center gap-2 text-sm text-shrimp-text hover:text-shrimp-green transition-colors"
                data-testid="account-link"
              >
                <User className="w-5 h-5" />
                <span className="hidden lg:inline">{customer?.name?.split(' ')[0]}</span>
              </Link>
            ) : (
              <Link 
                to="/customer-login" 
                className="flex items-center gap-1 text-sm text-shrimp-text hover:text-shrimp-green transition-colors"
                data-testid="login-link"
              >
                <User className="w-5 h-5" />
                <span className="hidden lg:inline">Sign In</span>
              </Link>
            )}
            
            <CartButton />
          </div>

          {/* Mobile Menu Button & Cart */}
          <div className="flex items-center gap-1 md:hidden">
            <CartButton />
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="h-11 w-11 flex items-center justify-center rounded-full text-shrimp-dark active:bg-shrimp-beige"
              data-testid="mobile-menu-button"
              aria-label={isMenuOpen ? "Close menu" : "Open menu"}
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <nav className="md:hidden py-4 border-t border-shrimp-border/50" data-testid="mobile-nav">
            <div className="flex flex-col gap-2">
              <Link
                to="/"
                onClick={() => setIsMenuOpen(false)}
                className={`px-4 py-3 rounded-lg font-body font-medium transition-colors ${
                  location.pathname === '/'
                    ? 'bg-shrimp-beige text-shrimp-dark'
                    : 'text-shrimp-text hover:bg-shrimp-beige/50'
                }`}
              >
                Home
              </Link>
              
              <MobileDropdown
                label="Shrimp Room"
                items={shrimpRoomItems}
                isActive={isShrimpRoomActive}
                onItemClick={() => setIsMenuOpen(false)}
              />

              <MobileDropdown
                label="Shrimp Needs"
                items={shrimpNeedsItems}
                isActive={isShrimpNeedsActive}
                onItemClick={() => setIsMenuOpen(false)}
              />
              
              <MobileDropdown
                label="About Us"
                items={aboutUsItems}
                isActive={isAboutActive}
                onItemClick={() => setIsMenuOpen(false)}
              />
              
              <Link
                to="/contact"
                onClick={() => setIsMenuOpen(false)}
                className={`px-4 py-3 rounded-lg font-body font-medium transition-colors ${
                  isActive('/contact')
                    ? 'bg-shrimp-beige text-shrimp-dark'
                    : 'text-shrimp-text hover:bg-shrimp-beige/50'
                }`}
              >
                Contact
              </Link>

              <div className="flex items-center gap-4 px-4 py-3">
                {/* Dark mode toggle - mobile */}
                <button
                  onClick={() => setIsDark(!isDark)}
                  className="p-2 rounded-lg text-shrimp-text hover:text-shrimp-dark hover:bg-shrimp-beige transition-colors"
                  data-testid="dark-mode-toggle-mobile"
                  aria-label="Toggle dark mode"
                >
                  {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                </button>
                {settings.facebook_url && (
                  <a href={settings.facebook_url} target="_blank" rel="noopener noreferrer" className="text-shrimp-text">
                    <Facebook className="w-5 h-5" />
                  </a>
                )}
                {settings.instagram_url && (
                  <a href={settings.instagram_url} target="_blank" rel="noopener noreferrer" className="text-shrimp-text">
                    <Instagram className="w-5 h-5" />
                  </a>
                )}
                {settings.youtube_url && (
                  <a href={settings.youtube_url} target="_blank" rel="noopener noreferrer" className="text-shrimp-text hover:text-red-500">
                    <Youtube className="w-5 h-5" />
                  </a>
                )}
              </div>
              {user ? (
                <Link to="/admin" onClick={() => setIsMenuOpen(false)} className="px-4">
                  <Button className="w-full bg-shrimp-green hover:bg-shrimp-text" data-testid="mobile-admin-button">
                    Dashboard
                  </Button>
                </Link>
              ) : null}
              
              {/* Mobile Customer Account */}
              <div className="px-4 pt-2 border-t border-shrimp-border/50">
                {isAuthenticated ? (
                  <div className="flex items-center justify-between">
                    <Link 
                      to="/my-account" 
                      onClick={() => setIsMenuOpen(false)}
                      className="flex items-center gap-2 text-shrimp-dark font-medium"
                    >
                      <User className="w-5 h-5" />
                      {customer?.name}
                    </Link>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => { customerLogout(); setIsMenuOpen(false); }}
                      className="text-shrimp-text hover:text-red-600"
                    >
                      <LogOut className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Link to="/customer-login" onClick={() => setIsMenuOpen(false)} className="flex-1">
                      <Button variant="outline" className="w-full border-shrimp-border">
                        Sign In
                      </Button>
                    </Link>
                    <Link to="/register" onClick={() => setIsMenuOpen(false)} className="flex-1">
                      <Button className="w-full bg-shrimp-green hover:bg-shrimp-text">
                        Register
                      </Button>
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};
